import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import CardActionArea from "@material-ui/core/CardActionArea";
import Collapse from "@material-ui/core/Collapse";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Divider from "@material-ui/core/Divider";
import Tooltip from "@material-ui/core/Tooltip";
import LinearProgress from "@material-ui/core/LinearProgress";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import Chip from "@material-ui/core/Chip";

import MessageList from "./MessageList";

import { Message as MessageActions } from "../../../../redux/actions";

import { sanitize } from "../../../../lib/helpers";
import { setOwners } from "../../../../redux/actions/Findings";
import moment from "moment-timezone";

import { FLAG_TYPE, PRIORITY } from "../../../../design/colors";

const findingStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    overflowWrap: "break-word",
  },
  mainContent: {
    paddingTop: 0,
  },
  analysisBody: {
    marginBottom: "0.35em",
  },
  userMenuContainer: {
    zIndex: theme.zIndex.modal,
  },
  userMenu: {
    maxHeight: "25vh",
    overflowY: "auto",
  },
  expand: {
    transform: "rotate(0deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  cluster: {
    "&:hover *:not(:first-child)": {
      marginLeft: theme.spacing(0),
    },
    "& > *:not(:first-child)": {
      marginLeft: theme.spacing(-4),
    },
  },
  clusterAvatarItem: {
    transition: theme.transitions.create(["margin-left"], {
      duration: theme.transitions.duration.standard,
    }),
  },
  clusterAvatar: {
    border: "2px solid white",
  },
  expandOpen: {
    transform: "rotate(180deg)",
  },
  flagType10: {
    color: FLAG_TYPE["10"],
    fontWeight: 800,
  },
  flagType15: {
    color: FLAG_TYPE["15"],
    fontWeight: 800,
  },
  flagType20: {
    color: FLAG_TYPE["20"],
    fontWeight: 800,
  },
  flagType30: {
    color: FLAG_TYPE["30"],
    fontWeight: 800,
  },
  flagType40: {
    color: FLAG_TYPE["40"],
    fontWeight: 800,
  },
  priority1: {
    backgroundColor: PRIORITY["1"],
  },
  priority2: {
    backgroundColor: PRIORITY["2"],
  },
  priority3: {
    backgroundColor: PRIORITY["3"],
  },
}));

const FindingCard = (props) => {
  const classes = findingStyles();
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState(props.ownerUsers || {});
  const [owners, setFindingsOwners] = useState([]);
  const [removeOwner, setRemoveOwner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nextWorkflow, setNextWorkflow] = useState(null);
  const [messages, setMessages] = useState(false);

  useEffect(() => {
    if (props.workflows[0] && props.workflows[0].questions) {
      const nextQuestion = props.workflows[0].questions.find(
        (question) => question.answered === false
      );
      if (nextQuestion) {
        setNextWorkflow(nextQuestion.text);
      }
    } else {
      setNextWorkflow(null);
    }
  }, props.workflows);

  useEffect(() => {
    setMessages(props.comments);
  }, [props.comments]);

  useEffect(() => {
    const users = { ...props.ownerUsers };
    const _owners = Object.values(props.owners).reduce((all, x) => {
      x.map((uid) => {
        if (users[uid]) {
          all.push(users[uid]);
          delete users[uid];
        }
      });
      return all;
    }, []);
    setSaving(false);
    setUsers(users);
    setFindingsOwners(_owners);
  }, [props.ownerUsers]);

  const fetchComments = () => {
    setMessages(false);
    props.dispatch(
      MessageActions.getCommentsForFinding({
        findingId: props.id,
      })
    );
  };

  const handleExpandClick = () => {
    if (!expanded) {
      fetchComments();
    }
    setExpanded(!expanded);
  };

  const getText = (textType) => {
    if (!props.constants) return "";

    // this code is to support new areas
    // such as summary dashboard that retrieve
    // constants as a model
    if (Array.isArray(props.constants) && !!props.constants.length)
      return props.constants[0][textType].find((c) => c.id === props[textType])
        .name;

    // this code is to support legacy
    // areas such as responder dashboard
    if (props.constants[textType])
      return props.constants[textType].fromId(props[textType], "");
  };

  const typeText = getText("type");
  const categoryText = getText("category");

  const createdDateTime = moment
    .utc(props.created)
    .tz(moment.tz.guess(true))
    .format("lll z");

  const gotoFinding = () =>
    props.dispatch({
      type: "PAGE",
      payload: {
        orgId: props.orgId,
        toplevel: "reporting",
        secondlevel: "findings",
        id1: props.id,
      },
    });

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (user) => {
    if (user) {
      addOwner(user);
    }
    setAnchorEl(null);
  };

  const addOwner = (user) => {
    const _owners = (owners.concat([user]) || []).map((i) => i.id);
    setSaving(true);
    props.dispatch(
      setOwners({
        findingId: props.id,
        owners: _owners,
        ownerType: props.pageRole || "responder",
      })
    );
  };

  const confirmOwnerRemoval = (user) => {
    if (removeOwner === user.id) {
      const _owners = owners.filter((u) => u.id !== user.id).map((u) => u.id);
      setSaving(true);
      props.dispatch(
        setOwners({
          findingId: props.id,
          owners: _owners,
          ownerType: props.pageRole || "responder",
        })
      );
    } else {
      setRemoveOwner(user.id);
      setTimeout(() => setRemoveOwner(null), 3000);
    }
  };

  return (
    <Card className={classes.root} data-cy={`findingCard`}>
      <CardActionArea onClick={gotoFinding}>
        <CardHeader
          avatar={
            <Avatar
              variant="rounded"
              alt={typeText}
              className={classes[`priority${props.priority}`]}
            >
              P{props.priority}
            </Avatar>
          }
          action={<Typography variant="overline">{props.shortId}</Typography>}
          title={
            <div>
              <span className={classes[`flagType${props.type}`]}>
                {typeText}
              </span>
              : {props.name}
              {props.isSample ? (
                <Chip
                  label="Example Finding"
                  color="primary"
                  size="small"
                  variant="outlined"
                  style={{ marginLeft: 12, marginTop: -4 }}
                />
              ) : null}
            </div>
          }
          subheader={
            <span>
              {categoryText} on {createdDateTime}
            </span>
          }
        />
        <CardContent className={classes.mainContent}>
          <Typography gutterBottom variant="h6">
            Analysis
          </Typography>
          <Typography
            variant="body2"
            component="div"
            dangerouslySetInnerHTML={sanitize(props.analysis, {
              html: true,
              safe: true,
            })}
            className={classes.analysisBody}
          />
          <Typography gutterBottom variant="h6">
            Next Workflow Step
          </Typography>
          <Typography
            variant="body2"
            component="div"
            dangerouslySetInnerHTML={sanitize(nextWorkflow, {
              html: true,
              safe: true,
            })}
            className={classes.analysisBody}
          />
        </CardContent>
      </CardActionArea>
      <Divider light />
      {!props.hideComments && (
        <CardActions disableSpacing>
          <div className={classes.cluster}>
            {owners.length > 0 &&
              owners.map((owner) => (
                <Tooltip
                  key={owner.id}
                  title={`Remove Responder: ${owner.first_name} ${owner.last_name}`}
                  className={classes.clusterAvatarItem}
                >
                  <IconButton onClick={() => confirmOwnerRemoval(owner)}>
                    {removeOwner === owner.id ? (
                      <RemoveCircleOutlineIcon
                        color="secondary"
                        style={{ fontSize: 40 }}
                      />
                    ) : (
                      <Avatar
                        alt={`${owner.first_name} ${owner.last_name}`}
                        src={owner.picture}
                        className={classes.clusterAvatar}
                      />
                    )}
                  </IconButton>
                </Tooltip>
              ))}
            <Tooltip
              title="Add Responder"
              className={classes.clusterAvatarItem}
            >
              <IconButton onClick={handleClick} color="primary">
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Popper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            role={undefined}
            transition
            className={classes.userMenuContainer}
          >
            <Paper className={classes.userMenu}>
              <ClickAwayListener onClickAway={() => handleClose()}>
                <MenuList id={props.id}>
                  {Object.values(users).map((user, i) => (
                    <MenuItem
                      key={`${props.id}-user-${i}`}
                      onClick={() => handleClose(user)}
                    >
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
          <Tooltip title="Messages">
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expanded,
              })}
              onClick={handleExpandClick}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider light variant="middle" />
        <CardContent>
          <MessageList
            messages={messages}
            dispatch={props.dispatch}
            orgId={props.orgId}
            findingId={props.id}
            currentUser={props.currentUser}
          />
        </CardContent>
      </Collapse>

      {saving && <LinearProgress />}
    </Card>
  );
};

const mapStateToProps = (state, props) => {
  return {
    comments:
      state.message && state.message.getIn
        ? state.message.getIn(["messages", props.id], [])
        : [],
    currentUser: state.session.settings.user.id,
  };
};

export default connect(mapStateToProps)(FindingCard);
